import React from 'react';
import StaticPageLayout from '../components/StaticPageLayout';

const RevisionPolicyPage = () => (
  <StaticPageLayout
    overline="Submission Policies"
    title="Revision Policy"
    description="When a manuscript requires revisions, authors are provided with structured reviewer feedback and a defined timeline to respond. This page outlines the revision process and expectations."
    breadcrumbs={[{ label: 'Submission Policies', path: '/submission-process' }, { label: 'Revision Policy', path: '/revision-policy' }]}
  >
    <div className="space-y-10">
      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-5 pb-3 border-b border-[#E5E7EB]">Types of Revision Decisions</h2>
        <div className="space-y-3">
          {[
            { type: 'Minor Revision', timeline: '3 weeks', desc: 'Minor revisions require targeted improvements to specific sections of the manuscript — such as clarification of methodology, correction of minor errors, or enhancement of figure quality. A revised manuscript addressing minor revisions may be accepted without a second round of peer review.' },
            { type: 'Major Revision', timeline: '8 weeks', desc: 'Major revisions require substantial reworking of the manuscript. This may include additional experiments or data, restructuring of the argument, or significant rewriting. Manuscripts undergoing major revision will typically re-enter the peer review process.' },
          ].map(item => (
            <div key={item.type} className="border border-[#E5E7EB] p-5 bg-white">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-[#1F1F1F] text-sm">{item.type}</h3>
                <span className="text-xs text-[#6B7280] border border-[#E5E7EB] px-2 py-0.5">Response deadline: {item.timeline}</span>
              </div>
              <p className="text-sm text-[#6B7280] leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">How to Respond to Revisions</h2>
        <p className="text-[#4B5563] leading-relaxed mb-4">
          When submitting a revised manuscript, authors are required to provide a <strong>point-by-point response letter</strong> addressing each reviewer comment. The response letter must:
        </p>
        <ul className="space-y-2 text-sm text-[#4B5563]">
          {[
            'Quote each reviewer comment verbatim, then provide a direct response below it',
            'Clearly indicate where changes have been made in the manuscript (with page and line numbers)',
            'Provide a reasoned, professional justification for any reviewer suggestions that were not adopted',
            'Include a clean revised manuscript and, where helpful, a tracked-changes version',
          ].map(item => (
            <li key={item} className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 bg-[#1A73E8] rounded-full flex-shrink-0 mt-1.5" />
              {item}
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">Late or Incomplete Responses</h2>
        <p className="text-[#4B5563] leading-relaxed">
          If a revised manuscript is not received within the stated deadline, the submission may be treated as a withdrawal. Authors who require additional time should contact the editorial office before the deadline expires. Extensions are considered on a case-by-case basis.
        </p>
      </section>
    </div>
  </StaticPageLayout>
);

export default RevisionPolicyPage;
